<template>
  <div class="container my-5">
    <!-- Zurück-Button -->
    <button @click="goBack" class="btn btn-secondary mb-3">
      <font-awesome-icon icon="arrow-left" /> Zurück zur Übersicht
    </button>

    <h2 class="text-center mb-4">Verkauf starten</h2>

    <!-- Artikel hinzufügen -->
    <div class="row justify-content-center">
      <div class="col-12 col-md-6 col-lg-4 mb-4">
        <div class="modern-tile">
          <input type="text" v-model="articleIdInput" @keyup.enter="addArticle" class="form-control"
            placeholder="Artikelnummer eingeben" />
        </div>
      </div>
    </div>

    <!-- Warenkorb anzeigen -->
    <div v-if="cartItems.length > 0" class="row justify-content-center mt-4">
      <div class="col-12 col-md-8 col-lg-6">
        <h3 class="text-center mb-4">Artikel im Warenkorb</h3>
        <!-- Verkaufsdatum wählen -->
        <div class="row justify-content-center mb-3">
          <div class="col-12 col-md-6 col-lg-4">
            <label for="sale-date">Verkaufsdatum</label>
            <input id="sale-date" type="date" class="form-control" v-model="selectedSaleDate" :max="today" />
          </div>
        </div>
        <ul class="list-group">
          <li v-for="(item, index) in cartItems" :key="item.ArticleId" class="list-group-item">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                {{ item.ArticleId }} - {{ item.Name }} ({{ item.Color }})
              </div>
              <div @click="removeItem(item.ArticleId)" class="remove-item">✖️</div>
            </div>
            <div class="discount-section mt-2 d-flex align-items-center">
              <label class="mr-2">Rabatt (%)</label>
              <input type="number" v-model.number="item.discount" @change="updateDiscount(item)"
                class="form-control w-25" min="0" max="100" />
              <span class="ml-3">
                Neuer Preis: {{ $formatPrice(computeFinalPrice(item)) }}
              </span>
            </div>
          </li>
        </ul>
        <div class="cart-wide-discount mb-4 p-3 bg-light rounded">
          <h6 class="mb-2">Rabatt auf gesamten Warenkorb</h6>
          <div class="d-flex flex-wrap align-items-center gap-2">
            <div class="form-check form-check-inline">
              <input id="disc-type-pct" type="radio" v-model="cartDiscountType" value="percent" class="form-check-input" />
              <label for="disc-type-pct" class="form-check-label">Prozent (%)</label>
            </div>
            <div class="form-check form-check-inline">
              <input id="disc-type-amt" type="radio" v-model="cartDiscountType" value="amount" class="form-check-input" />
              <label for="disc-type-amt" class="form-check-label">Betrag (CHF)</label>
            </div>
            <input type="number" v-model.number="cartDiscountValue" class="form-control" style="max-width: 100px;"
              :min="0" :max="cartDiscountType === 'percent' ? 100 : totalAmount"
              :step="cartDiscountType === 'percent' ? 1 : 0.01"
              placeholder="" />
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="applyCartWideDiscount">
              Rabatt anwenden
            </button>
          </div>
        </div>
        <div class="summary mt-4 text-center">
          <h4>Gesamtbetrag: {{ $formatPrice(totalAmount) }}</h4>
          <button @click="checkoutSale" class="btn btn-primary btn-block mt-3">
            Bezahlen & Verkauf abschließen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/services/axios';

export default {
  name: 'SalesPage',
  data() {
    return {
      articleIdInput: '',
      cartItems: [],
      selectedSaleDate: this.getTodayDate(),
      cartDiscountType: 'percent',
      cartDiscountValue: null
    };
  },
  computed: {
    today() {
      return this.getTodayDate();
    },
    totalAmount() {
      return this.cartItems.reduce((sum, item) => {
        return sum + parseFloat(this.computeFinalPrice(item));
      }, 0);
    }
  },
  methods: {
    goBack() {
      this.$router.push('/mainmenu');
    },
    getTodayDate() {
      // Gibt yyyy-MM-dd zurück (HTML5 date format)
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    async addArticle() {
      if (!this.articleIdInput) return;
      try {
        await axios.post('/cart/add', { articleId: this.articleIdInput });
        this.articleIdInput = '';
        this.getCart();
      } catch (error) {
        console.error("Fehler beim Hinzufügen des Artikels:", error);
        if (
          error.response &&
          error.response.status === 400 &&
          error.response.data.message === "Artikel wurde bereits verkauft."
        ) {
          alert("⚠️ Dieser Artikel wurde bereits verkauft und kann nicht hinzugefügt werden.");
        } else {
          alert("Artikel konnte nicht hinzugefügt werden.");
        }
      }
    },

    async getCart() {
      try {
        const response = await axios.get('/cart');
        this.cartItems = response.data.map(item => {
          const base = item.SalePrice || 0;
          const discPrice = item.PriceDiscountLevel1;
          const discount = (discPrice != null && base > 0)
            ? Math.round((1 - discPrice / base) * 1000) / 10
            : 0;
          return {
            ...item,
            discount,
            finalPrice: discPrice ?? base
          };
        });
      } catch (error) {
        console.error("Fehler beim Laden des Warenkorbs:", error);
      }
    },

    async removeItem(articleId) {
      try {
        await axios.delete(`/cart/remove/${articleId}`);
        this.getCart();
      } catch (error) {
        console.error("Fehler beim Entfernen des Artikels:", error);
      }
    },

    computeFinalPrice(item) {
      const final = item.SalePrice * (1 - (item.discount || 0) / 100);
      return this.$roundToFranc(final);
    },

    async updateDiscount(item) {
      try {
        const newPrice = parseFloat(this.computeFinalPrice(item));
        await axios.put(`/articles/${item.ArticleId}`, {
          PriceDiscountLevel1: newPrice,
          DiscountPercentageLevel1: item.discount,
          DiscountStatus: item.discount > 0 ? 'level 1' : 'no discount'
        });
        item.finalPrice = newPrice;
      } catch (error) {
        console.error("Fehler beim Aktualisieren des Rabatts:", error);
      }
    },

    async applyCartWideDiscount() {
      const val = parseFloat(this.cartDiscountValue);
      if (!val || val <= 0) return;
      const total = parseFloat(this.totalAmount);
      if (total <= 0) return;

      let totalDiscount;
      if (this.cartDiscountType === 'percent') {
        totalDiscount = Math.min(total * (val / 100), total);
      } else {
        totalDiscount = Math.min(val, total);
      }

      if (totalDiscount <= 0) return;

      try {
        const roundF = (v) => Math.max(0, this.$roundToFranc(v));
        const updates = this.cartItems.map(item => {
          const itemPrice = parseFloat(this.computeFinalPrice(item));
          const share = totalDiscount * (itemPrice / total);
          const newPrice = roundF(itemPrice - share);
          return { item, newPrice };
        });
        if (this.cartItems.length > 1) {
          const newTotal = updates.reduce((s, u) => s + u.newPrice, 0);
          const targetTotal = this.$roundToFranc(total - totalDiscount);
          const diff = newTotal - targetTotal;
          if (Math.abs(diff) >= 1) {
            updates[updates.length - 1].newPrice = roundF(updates[updates.length - 1].newPrice - diff);
          }
        }
        for (const { item, newPrice } of updates) {
          const pct = item.SalePrice > 0 ? Math.round((1 - newPrice / item.SalePrice) * 1000) / 10 : 0;
          await axios.put(`/articles/${item.ArticleId}`, {
            PriceDiscountLevel1: newPrice,
            DiscountPercentageLevel1: pct,
            DiscountStatus: pct > 0 ? 'level 1' : 'no discount'
          });
          item.discount = pct;
          item.finalPrice = newPrice;
        }
        this.cartDiscountValue = null;
      } catch (error) {
        console.error("Fehler beim Anwenden des Rabatts:", error);
        alert("Rabatt konnte nicht angewendet werden.");
      }
    },

    async checkoutSale() {
      try {
        // Datum (string) mitsenden!
        const response = await axios.post('/cart/checkout', {
          saleDate: this.selectedSaleDate // yyyy-MM-dd
        });
        if (response.data.message) {
          alert("Verkauf erfolgreich abgeschlossen!");
          this.cartItems = [];
          // Optional Datum resetten
          this.selectedSaleDate = this.getTodayDate();
        }
      } catch (error) {
        console.error("Fehler beim Abschluss des Verkaufs:", error);
        alert("Verkauf konnte nicht abgeschlossen werden.");
      }
    }
  },

  mounted() {
    this.getCart();
  }
};
</script>


<style scoped>
.btn {
  margin-bottom: 20px;
  font-size: 16px;
  padding: 10px 20px;
  border-radius: 8px;
}

.modern-tile {
  background-color: var(--primary-color);
  color: white;
  padding: 20px;
  border-radius: 20px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.list-group-item {
  margin-bottom: 10px;
}

.remove-item {
  cursor: pointer;
  color: red;
}

.discount-section input {
  max-width: 80px;
}

.summary {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}
</style>